/** @format — Documents tab inside shipment detail drawer */

import { useState } from "react";
import DocumentGridCard from "@/pages/documents/components/DocumentGridCard";
import DocumentsExpectedGrid from "@/pages/documents/components/DocumentsExpectedGrid";
import DocumentsOverdueGrid from "@/pages/documents/components/DocumentsOverdueGrid";
import DocumentsGridSkeleton from "@/pages/documents/components/DocumentsGridSkeleton";
import DocumentsStatusTabs from "@/pages/documents/components/DocumentsStatusTabs";
import type { DocumentItem } from "@/pages/documents/types";
import {
  useShipmentDocuments,
  type ShipmentDocumentTab,
} from "../hooks/useShipmentDocuments";

export default function ShipmentDocumentsPanel({
  listingId,
  onOpenDocument,
}: {
  listingId: string;
  onOpenDocument: (doc: DocumentItem) => void;
}) {
  const [activeTab, setActiveTab] = useState<ShipmentDocumentTab>("received");
  const { received, expected, overdue, counts, isLoading } = useShipmentDocuments(listingId);

  const countLabel =
    activeTab === "received"
      ? `${received.length} items`
      : activeTab === "expected"
        ? `${expected.filter((item) => item.exists).length} of ${expected.length} documents received`
        : `${overdue.filter((item) => !item.exists).length} documents overdue`;

  return (
    <div className="flex flex-col gap-4 px-4 py-4 pr-2">
      <DocumentsStatusTabs
        variant="drawer"
        activeTab={activeTab}
        onChange={setActiveTab}
        counts={counts}
      />

      {!isLoading ? (
        <p className="text-xs font-semibold uppercase text-[#344054]">{countLabel}</p>
      ) : null}

      {isLoading ? (
        activeTab === "received" || activeTab === "expected" ? (
          <DocumentsGridSkeleton count={6} columns={3} />
        ) : (
          <DocumentsGridSkeleton count={4} columns={2} />
        )
      ) : activeTab === "received" ? (
        received.length === 0 ? (
          <p className="py-12 text-center text-sm text-neutral-500">No documents in this tab yet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {received.map((document) => (
              <DocumentGridCard
                key={document.id}
                document={document}
                onOpen={onOpenDocument}
                showGoToShipment={false}
              />
            ))}
          </div>
        )
      ) : activeTab === "expected" ? (
        <DocumentsExpectedGrid
          items={expected}
          onViewDocument={(url) => window.open(url, "_blank", "noopener,noreferrer")}
        />
      ) : (
        <DocumentsOverdueGrid items={overdue} />
      )}
    </div>
  );
}
